import { useCallback, useLayoutEffect, useRef } from 'react';


/**
 * When you have a callback that references changing state, you can use this hook to wrap it in a ref.
 * This way, the callback remains constant, even when your state's value changes. This prevents components that reference
 * the callback (but not the state) from re-rendering every time the state changes.
 *
 * @example
 * const [taters, setTaters] = useState(props.taters);
 *
 * // This callback changes everytime the value of `taters` changes
 * const onTaterSelected = useCallback(
 *   (event) => setTaters(taters.concat(event.target.value)),
 *   [taters],
 * );
 *
 * // Becomes
 *
 * const [taters, setTaters] = useState(props.taters);
 *
 * // This callback never changes;
 * // instead, when called, it delegates internally to the most recent function it was given
 * const onTaterSelected = useRefCallback(
 *   (event) => setTaters(taters.concat(event.target.value)),
 * );
 */
export const useRefCallback = <T extends (...args: any[]) => any>(fn: T) => {
  const ref = useRef<T>(fn);

  useLayoutEffect(() => { ref.current = fn; });

  return useCallback((...args) => ref.current(...args), []);
};
